import { APP_INITIALIZER, Injector, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { ButtonModule } from 'primeng/button';
import { ConfigService } from './_services/config.service';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { HttpRequestInterceptor } from './_services/http-request-interceptor';
import { SharingModule } from './sharing/sharing.module';
import { FunzioniRuoliModule } from './_directives/funzioni-ruoli.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, DatePipe, HashLocationStrategy, LocationStrategy, registerLocaleData } from '@angular/common';
import { NgxSpinnerModule } from 'ngx-spinner';
import { NumberFormatPipeModule } from './sharing/_pipes/numberFormatPipeModule';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { TabellaService } from './_services/tabella.service';
import { CustomTranslateLoader } from './components/customTranslate.loader';
import { CascadeSelectModule } from 'primeng/cascadeselect';
import { MenuModule } from 'primeng/menu';
import { providePrimeNG } from 'primeng/config';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { SkeletonModule } from 'primeng/skeleton';
import Aura from '@primeuix/themes/aura';
import { ModalModule } from 'ngx-bootstrap/modal';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TabsModule } from 'ngx-bootstrap/tabs';
import localeIt from '@angular/common/locales/it';
import { ColorPickerModule } from 'primeng/colorpicker';
import { SliderModule } from 'primeng/slider';
import { PopoverModule } from 'primeng/popover';
registerLocaleData(localeIt);

// Import containers
import { FullLayoutComponent, SimpleLayoutComponent } from './containers';

const APP_CONTAINERS = [FullLayoutComponent, SimpleLayoutComponent];

// Import components
import { AppBreadcrumbsComponent, AppFooterComponent, AppHeaderComponent, AppSidebarComponent, AppSidebarFooterComponent, AppSidebarFormComponent, AppSidebarHeaderComponent, AppSidebarMinimizerComponent, APP_SIDEBAR_NAV, CustomToastComponent } from './components';

const APP_COMPONENTS = [AppBreadcrumbsComponent, AppFooterComponent, AppHeaderComponent, AppSidebarComponent, AppSidebarFooterComponent, AppSidebarFormComponent, AppSidebarHeaderComponent, AppSidebarMinimizerComponent, APP_SIDEBAR_NAV, CustomToastComponent];

// Import directives
import { AsideToggleDirective, NAV_DROPDOWN_DIRECTIVES, ReplaceDirective, SIDEBAR_TOGGLE_DIRECTIVES } from './directives';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { DatePickerModule } from 'primeng/datepicker';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { P503Component } from './views/pages/503.component';
import { P404Component } from './views/pages/404.component';
import { TooltipModule } from 'primeng/tooltip';
import { AvatarModule } from 'primeng/avatar';
import { TagModule } from 'primeng/tag';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ProfiloComponent } from './portale/Profilo/profilo.component';
import { ServiceCardsComponent } from './components/service-cards/service-cards.component';
import { SelectModule } from 'primeng/select';
import { ListaAppuntamentiComponent } from './companies/ListaAppuntamenti/lista-appuntamenti.component';
import { FullCalendarModule } from '@fullcalendar/angular';
import { MultiSelectModule } from 'primeng/multiselect';
import { EventoAppuntamentoComponent } from './companies/ListaAppuntamenti/evento/evento-appuntamento.component';

const APP_DIRECTIVES = [AsideToggleDirective, NAV_DROPDOWN_DIRECTIVES, ReplaceDirective, SIDEBAR_TOGGLE_DIRECTIVES];

function initConfig(config: ConfigService) {
	return () => config.load();
}

export const httpInterceptorProviders = [{ provide: HTTP_INTERCEPTORS, useClass: HttpRequestInterceptor, multi: true }];

@NgModule({
	imports: [
		BrowserModule,
		AppRoutingModule,
		ButtonModule,
		CommonModule,
		ReactiveFormsModule,
		FormsModule,
		FunzioniRuoliModule,
		SharingModule,
		BrowserAnimationsModule,
		NgxSpinnerModule,
		HttpClientModule,
		NumberFormatPipeModule,
		ToastModule,
		CascadeSelectModule,
		PopoverModule,
		MenuModule,
		InputIconModule,
		SkeletonModule,
		IconFieldModule,
		DatePickerModule,
		CheckboxModule,
		ColorPickerModule,
		DividerModule,
		MultiSelectModule,
		FullCalendarModule,
		SliderModule,
		PopoverModule,
		TooltipModule,
		AvatarModule,
		TagModule,
		ServiceCardsComponent,
		RadioButtonModule,
		ProfiloComponent,
		SelectModule,
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useClass: CustomTranslateLoader,
				deps: [HttpClient, Injector],
			},
		}),
		BsDropdownModule,
		TabsModule,
		ModalModule,
	],
	declarations: [AppComponent, P404Component, P503Component, ...APP_CONTAINERS, ...APP_COMPONENTS, ...APP_DIRECTIVES,
		ListaAppuntamentiComponent,
				EventoAppuntamentoComponent,],

	providers: [
		provideAnimationsAsync(),
		providePrimeNG({
			theme: {
				preset: Aura,
				options: {
					prefix: 'p',
					darkModeSelector: '.app-dark',
					cssLayer: false,
				},
			},
			overlayOptions: {
				appendTo: 'body',
			},
		}),
		{
			provide: LocationStrategy,
			useClass: HashLocationStrategy,
		},
		{
			provide: APP_INITIALIZER,
			useFactory: (configService: ConfigService) =>
				function () {
					return initConfig(configService);
				},
			deps: [ConfigService],
			multi: true,
		},
		{
			provide: LOCALE_ID,
			useValue: 'it-IT',
		},

		HttpClient,
		TranslateService,
		ConfigService,
		TabellaService,
		DatePipe,
		MessageService,
		httpInterceptorProviders,
	],
	bootstrap: [AppComponent],
})
export class AppModule {}
